import { Descope } from '@descope/react-sdk/flows';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getAuthMfaChallengeRequestFailureReasonCode,
  publishAuthMfaChallengeRequestCompletedEvent,
  publishAuthMfaChallengeRequestedEvent,
} from '@shared/utils/analytics/events';
import { getOrCreateClientSessionId } from '@shared/utils/clientSessionId';
import { withClientSessionId } from '@shared/utils/descopeActionPayload';
import {
  ADD_MFA_WITH_SMS_OTP_INTERACTION_IDS,
  AddMfaWithSmsOtpScreenNames,
} from './screenMapping';
import OTPVerificationScreen from '../universal-login/screens/OTPVerificationScreen';
import PhoneLoginScreen from '../universal-login/screens/PhoneLoginScreen';

export const ADD_MFA_WITH_SMS_OTP_FLOW_ID = 'add-mfa-with-sms-otp';

const LINKTREE_BASE_URL =
  import.meta.env.VITE_LINKTREE_BASE_URL || 'https://linktr.ee';
const MFA_SMS_ALLOWED_COUNTRIES = ['US', 'AU', 'GB', 'CA'] as const;

type DescopeNext = (
  interactionId: string,
  payload?: Record<string, unknown>
) => Promise<unknown>;
type RawDescopeNext = (
  interactionId: string,
  payload: Record<string, unknown>
) => Promise<unknown>;

type FlowForm = Record<string, unknown>;
type DescopeContext = {
  error?: unknown;
  form?: FlowForm;
  [key: string]: unknown;
};
type FlowState = DescopeContext & {
  next?: DescopeNext;
  screenName?: string;
};

const getDescopeRedirectUrl = (): string => {
  const url = new URL(window.location.href);
  url.hash = '';
  return url.toString();
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object';

const getErrorText = (error: unknown): string | undefined => {
  if (!isRecord(error)) {
    return undefined;
  }

  return typeof error.text === 'string' ? error.text : undefined;
};

const getErrorDetail = (error: unknown): unknown =>
  isRecord(error) && 'detail' in error ? error.detail : error;

const resetErrorState = (
  setState: React.Dispatch<React.SetStateAction<FlowState>>
) =>
  setState(prevState => ({
    ...prevState,
    error: {},
  }));

const AddMfaWithSmsOtpFlow: React.FC = () => {
  const { t } = useTranslation();
  const [state, setState] = useState<FlowState>({ error: {} });
  const [form, setForm] = useState<FlowForm>({});
  const [descopeRedirectUrl] = useState(getDescopeRedirectUrl);
  const [clientSessionId] = useState(getOrCreateClientSessionId);
  const flowForm = {
    ...(state?.form ?? {}),
    ...form,
  };

  return (
    <Descope
      flowId={ADD_MFA_WITH_SMS_OTP_FLOW_ID}
      styleId="linktree"
      theme="light"
      redirectUrl={descopeRedirectUrl}
      externalRequestId={clientSessionId}
      form={withClientSessionId(flowForm)}
      onScreenUpdate={(
        screenName: string,
        context: DescopeContext,
        next: RawDescopeNext
      ) => {
        const nextWithClientSessionId: DescopeNext = (interactionId, payload) =>
          next(interactionId, withClientSessionId(payload ?? {}));

        setState(prevState => ({
          ...prevState,
          ...context,
          next: nextWithClientSessionId,
          screenName,
          error: context?.error || {},
        }));

        const customScreenNames = [
          AddMfaWithSmsOtpScreenNames.PHONE_NUMBER,
          AddMfaWithSmsOtpScreenNames.VERIFY_OTP,
        ];

        return customScreenNames.includes(
          screenName as AddMfaWithSmsOtpScreenNames
        );
      }}
      onSuccess={() => {
        window.location.href = `${LINKTREE_BASE_URL}/admin/account`;
      }}
      onError={(error: unknown) => {
        setState(prevState => ({
          ...prevState,
          error: getErrorDetail(error),
        }));
      }}
    >
      {state?.screenName === AddMfaWithSmsOtpScreenNames.PHONE_NUMBER && (
        <PhoneLoginScreen
          authFlow="signup"
          allowedCountries={[...MFA_SMS_ALLOWED_COUNTRIES]}
          defaultCountry="US"
          heading={t('Add SMS verification')}
          subHeading={t('Enter your phone number to receive a one-time code.')}
          submitLabel={t('Send code')}
          variant="default"
          showCookiePrefs={false}
          publishViewEvents={false}
          onClickContinue={async (phone: string) => {
            const nextForm = { ...flowForm, phone };
            setForm(nextForm);

            if (state.next) {
              publishAuthMfaChallengeRequestedEvent(
                'signup',
                'initial_route',
                'sms',
                'enrollment'
              );

              try {
                await state.next(
                  ADD_MFA_WITH_SMS_OTP_INTERACTION_IDS.SUBMIT_PHONE,
                  nextForm
                );
                publishAuthMfaChallengeRequestCompletedEvent(
                  'signup',
                  'initial_route',
                  'succeeded',
                  'sms',
                  'enrollment'
                );
              } catch (error) {
                publishAuthMfaChallengeRequestCompletedEvent(
                  'signup',
                  'initial_route',
                  'failed',
                  'sms',
                  'enrollment',
                  getAuthMfaChallengeRequestFailureReasonCode(error)
                );
                throw error;
              }
            }
          }}
          errorText={getErrorText(state.error)}
          context={{ ...state, form: flowForm }}
          resetError={() => resetErrorState(setState)}
        />
      )}
      {state?.screenName === AddMfaWithSmsOtpScreenNames.VERIFY_OTP && (
        <OTPVerificationScreen
          authFlow="signup"
          otpChannel="sms"
          mfaFlow="enrollment"
          onFormUpdate={data => setForm(prevForm => ({ ...prevForm, ...data }))}
          onOTPVerificationClick={async (code: string) => {
            if (state.next) {
              await state.next(
                ADD_MFA_WITH_SMS_OTP_INTERACTION_IDS.SUBMIT_CODE,
                {
                  ...flowForm,
                  code,
                }
              );
            }
          }}
          onClickResend={async () => {
            if (state.next) {
              await state.next(
                ADD_MFA_WITH_SMS_OTP_INTERACTION_IDS.RESEND,
                flowForm
              );
            }
          }}
          onClickUseDifferentNumber={async () => {
            if (state.next) {
              await state.next(
                ADD_MFA_WITH_SMS_OTP_INTERACTION_IDS.USE_DIFFERENT_NUMBER,
                flowForm
              );
            }
          }}
          errorText={getErrorText(state.error)}
          context={{ ...state, form: flowForm }}
          resetError={() => resetErrorState(setState)}
        />
      )}
    </Descope>
  );
};

export default AddMfaWithSmsOtpFlow;
